import React from 'react';
import { Box, Chip, Divider, Stack, Typography } from '@mui/material';
import type { ProjectThemeV2 } from '@modules/firebase-core/services/ProjectThemeService';
import type { ThemeV2 } from '@modules/themes/types/themesV2.types';

interface TokenSummaryItem {
  key: string;
  label: string;
  value: string;
  color?: string;
}

function formatTokenNumber(value: number | undefined): string | null {
  if (value === undefined || !Number.isFinite(value)) return null;
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

function getThemeTokenSummary(theme: Partial<ProjectThemeV2> | ThemeV2): TokenSummaryItem[] {
  const items: Array<TokenSummaryItem | null> = [
    theme.colors?.buttonsBackground
      ? { key: 'button-color', label: 'Button', value: theme.colors.buttonsBackground, color: theme.colors.buttonsBackground }
      : null,
    theme.colors?.titlesSubtitles
      ? { key: 'text-color', label: 'Text', value: theme.colors.titlesSubtitles, color: theme.colors.titlesSubtitles }
      : null,
    theme.colors?.buttonsTitle
      ? { key: 'button-text', label: 'Button text', value: theme.colors.buttonsTitle, color: theme.colors.buttonsTitle }
      : null,
    formatTokenNumber(theme.layoutSettings?.actionBar?.primaryButton?.height)
      ? { key: 'button-height', label: 'Button H', value: formatTokenNumber(theme.layoutSettings?.actionBar?.primaryButton?.height)! }
      : null,
    formatTokenNumber(theme.layoutSettings?.actionBar?.primaryButton?.radius)
      ? { key: 'button-radius', label: 'Button R', value: formatTokenNumber(theme.layoutSettings?.actionBar?.primaryButton?.radius)! }
      : null,
    formatTokenNumber(theme.layoutSettings?.optionsList?.itemRadius)
      ? { key: 'option-radius', label: 'Option R', value: formatTokenNumber(theme.layoutSettings?.optionsList?.itemRadius)! }
      : null,
    formatTokenNumber(theme.layoutSettings?.optionsList?.itemsSpacing)
      ? { key: 'option-gap', label: 'Option gap', value: formatTokenNumber(theme.layoutSettings?.optionsList?.itemsSpacing)! }
      : null,
    formatTokenNumber(theme.layoutSettings?.progress?.indicatorHeight)
      ? { key: 'progress-height', label: 'Progress H', value: formatTokenNumber(theme.layoutSettings?.progress?.indicatorHeight)! }
      : null,
    formatTokenNumber(theme.fonts?.title?.fontSize)
      ? { key: 'title-size', label: 'Title', value: formatTokenNumber(theme.fonts?.title?.fontSize)! }
      : null,
  ];

  return items.filter((item): item is TokenSummaryItem => Boolean(item));
}

export function ThemeTokenSummaryChips({ theme }: { theme: Partial<ProjectThemeV2> | ThemeV2 }) {
  const items = getThemeTokenSummary(theme);
  if (items.length === 0) return null;

  return (
    <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 0.75 }}>
      {items.map((item) => (
        <Chip
          key={item.key}
          size="small"
          label={`${item.label}: ${item.value}`}
          sx={{
            fontSize: 11,
            ...(item.color ? {
              '&::before': {
                content: '""',
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: item.color,
                border: '1px solid rgba(255,255,255,0.45)',
                mr: 0.5,
              },
            } : {}),
          }}
        />
      ))}
    </Stack>
  );
}

export function GeneratedThemeMetadataPanel({ theme }: { theme: ThemeV2 }) {
  const metadata = theme.generatedMetadata;
  const projectionPatchJson = metadata?.projectionPatch
    ? JSON.stringify(metadata.projectionPatch, null, 2)
    : null;
  const measurementBasisJson = metadata?.measurementBasis
    ? JSON.stringify(metadata.measurementBasis, null, 2)
    : null;
  const measurementBasis = metadata?.measurementBasis as {
    calibrationReferenceId?: string;
    observedScreenCount?: number;
    scaleX?: number;
    scaleY?: number;
  } | undefined;

  return (
    <Box
      data-testid="theme-v2-generated-metadata-panel"
      sx={{
        px: 3,
        py: 2,
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: 'background.default',
      }}
    >
      <Stack spacing={1.5}>
        <Box>
          <Typography variant="subtitle2">Generated draft verification</Typography>
          {metadata?.analysisSummary && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {metadata.analysisSummary}
            </Typography>
          )}
        </Box>

        <ThemeTokenSummaryChips theme={theme} />

        {metadata && (
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            {metadata.model && <Chip size="small" label={`Model: ${metadata.model}`} />}
            {metadata.confidence !== undefined && <Chip size="small" label={`Confidence: ${metadata.confidence}`} />}
            {metadata.screenInventoryCount !== undefined && <Chip size="small" label={`Screens: ${metadata.screenInventoryCount}`} />}
            {metadata.evidenceCount !== undefined && <Chip size="small" label={`Evidence: ${metadata.evidenceCount}`} />}
            {measurementBasis?.calibrationReferenceId && <Chip size="small" label="Calibrated" />}
            {measurementBasis?.scaleX !== undefined && measurementBasis?.scaleY !== undefined && (
              <Chip size="small" label={`Scale: ${measurementBasis.scaleX} x ${measurementBasis.scaleY}`} />
            )}
            {metadata.analysisId && <Chip size="small" label={`Analysis: ${metadata.analysisId}`} />}
          </Stack>
        )}

        {metadata?.warnings?.length ? (
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            {metadata.warnings.map((warning) => (
              <Chip key={warning} size="small" color="warning" label={warning} />
            ))}
          </Stack>
        ) : null}

        <Divider />

        {measurementBasisJson && (
          <Box>
            <Typography variant="caption" color="text.secondary">
              Measurement basis
            </Typography>
            <Box
              component="pre"
              sx={{
                mt: 0.75,
                mb: 0,
                p: 1.5,
                maxHeight: 160,
                overflow: 'auto',
                borderRadius: 1,
                border: 1,
                borderColor: 'divider',
                fontSize: 12,
                lineHeight: 1.45,
                whiteSpace: 'pre-wrap',
                bgcolor: 'background.paper',
              }}
            >
              {measurementBasisJson}
            </Box>
          </Box>
        )}

        <Box>
          <Typography variant="caption" color="text.secondary">
            Server projection patch
          </Typography>
          {projectionPatchJson ? (
            <Box
              component="pre"
              sx={{
                mt: 0.75,
                mb: 0,
                p: 1.5,
                maxHeight: 220,
                overflow: 'auto',
                borderRadius: 1,
                border: 1,
                borderColor: 'divider',
                fontSize: 12,
                lineHeight: 1.45,
                whiteSpace: 'pre-wrap',
                bgcolor: 'background.paper',
              }}
            >
              {projectionPatchJson}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
              This draft was created before server projection values were stored.
            </Typography>
          )}
        </Box>
      </Stack>
    </Box>
  );
}
